import type { FilterFn } from "@tanstack/vue-table";
import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { FlexComponent } from "@/utils/render-component";
import { debounce } from "lodash-es";
import { RadioGroupItem, RadioGroupRoot } from "reka-ui";
import { computed, defineComponent, ref, watch } from "vue";
import { $t } from "@/services/i18n";
import { p } from "@/utils/props-builder";
import Dropdown from "../../../primitives/dropdown/Dropdown";
import Badge from "../../badge/Badge";
import Button from "../../button/Button";
import FacetedFilter from "../../faceted-filter/FacetedFilter";
import Icon from "../../icon/Icon";
import Separator from "../../separator/Separator";
import { useTableEmitter } from "../composables/helpers";
import { injectTable } from "../DataTable";
import { defaultFacetedProps } from "./props";

export const operatorOptions = [
  {
    value: "like",
    symbol: "⊃",
    text: () => $t("contains"),
  },
  {
    value: "nlike",
    symbol: "⊅",
    text: () => $t("not_contains"),
  },
  {
    value: "regexp",
    symbol: "∼",
    text: () => $t("regex"),
  },
  {
    value: "eq",
    symbol: "=",
    text: () => $t("equal"),
  },
  {
    value: "neq",
    symbol: "≠",
    text: () => $t("not_equal"),
  },
] as const;

export type StringOperator = (typeof operatorOptions)[number]["value"];

export type StringFilter = {
  _type: "string";
  operator: StringOperator;
  value: string;
};

function formatStringFilter(operator: StringOperator, value: string): StringFilter {
  return {
    _type: "string",
    operator,
    value,
  };
}

const stringFilterProps = {
  ...defaultFacetedProps,
  placeholder: p.string($t("type_here")),
  operators: p.array<StringOperator[]>(["like", "nlike", "eq", "neq", "regexp"]),
} satisfies ComponentPropsOptions;

export type StringFacetedFilterProps = ExtractPublicPropTypes<typeof stringFilterProps>;

export default defineComponent({
  name: "StringFactedFilter",
  props: stringFilterProps,
  setup(props) {
    const table = injectTable();
    const column = table.getColumn(props.accessorKey);
    const operator = ref(props.operators[0]);
    const value = ref("");
    const inputRef = ref<HTMLInputElement>();

    function focusOnInput() {
      inputRef.value?.focus();
    }

    const columnFilter = computed(() => {
      const filter = column?.getFilterValue();

      if (!isStringFilter(filter)) return undefined;
      return filter;
    });

    watch(columnFilter, (val) => {
      if (!val) {
        value.value = "";
        return;
      }
    });

    watch(operator, (val, oldVal) => {
      if (!val) {
        operator.value = oldVal;
      }
    });

    watch(
      [operator, value],
      debounce(([operator, value]) => {
        if (!column) return;

        if (!value) column.setFilterValue(null);
        else column.setFilterValue(formatStringFilter(operator, value));
      }, props.debounce)
    );

    const operators = computed(() => {
      return props.operators.map((option) => {
        return {
          value: option,
          symbol: operatorOptions.find((i) => i.value === option)?.symbol ?? "",
          text: operatorOptions.find((i) => i.value === option)?.text?.() ?? "",
        };
      });
    });

    const Badges = computed<FlexComponent | undefined>(() => {
      if (!value.value) return undefined;

      const demarcator =
        operators.value.find((i) => i.value === operator.value)?.text === $t("regex") ? "/" : '"';

      const text = `${
        operators.value.find((i) => i.value === operator.value)?.text
      } ${demarcator}${value.value}${demarcator}`;

      return (
        <Badge variant="muted" size="xs">
          {text}
        </Badge>
      );
    });

    function selectNextOperator() {
      const index = operators.value.findIndex((i) => i.value === operator.value);

      const nextIndex = index + (1 % operators.value.length);

      if (!operators.value[nextIndex]) {
        operator.value = operators.value[0].value;
        return;
      }

      operator.value = operators.value[nextIndex].value;
    }

    function selectPreviousOperator() {
      const index = operators.value.findIndex((i) => i.value === operator.value);

      const nextIndex = index - 1 < 0 ? operators.value.length - 1 : index - 1;

      if (!operators.value[nextIndex]) return;

      operator.value = operators.value[nextIndex].value;
    }

    useTableEmitter("set-filter", (filter) => {
      if (filter.id !== table.id || filter.column !== props.accessorKey) return;

      const newVal = filter.setter({
        value: value.value,
        operator: operator.value,
      });

      if (newVal) {
        value.value = newVal.value;

        if (newVal.operator) {
          operator.value = newVal.operator as any;
        }
      }
    });

    return () => (
      <FacetedFilter
        label={props.label}
        badges={Badges.value}
        size={props.size}
        active={!!value.value}
      >
        <Dropdown.Group>
          <div class="flex items-center px-3">
            <Icon icon="lucide:search" class="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              class="dark:placeholder:text-gray-400 flex h-10 w-full rounded-md border-none bg-transparent px-0 py-3 text-sm outline-hidden placeholder:text-muted-foreground focus:border-none focus:outline-hidden focus:ring-0 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
              v-model={value.value}
              placeholder={props.placeholder}
              onKeydown={(e) => {
                if (e.code === "ArrowUp") selectPreviousOperator();
                if (e.code === "ArrowDown") selectNextOperator();
              }}
              maxlength={255}
            ></input>
          </div>

          {operators.value.length > 1 && (
            <div class="flex w-full">
              <RadioGroupRoot
                v-model={operator.value}
                class="flex w-full justify-between space-x-1 border-t p-1"
              >
                {operators.value.map((option) => {
                  return (
                    <RadioGroupItem
                      value={option.value}
                      class="focus:outline-text-muted-foreground/40 center w-full rounded-sm py-1.5 outline-hidden hover:backdrop-brightness-110 focus:outline-dashed focus:outline-1 focus:-outline-offset-1 focus:backdrop-brightness-95 data-[state=checked]:bg-muted"
                    >
                      {option.symbol}
                    </RadioGroupItem>
                  );
                })}
              </RadioGroupRoot>
            </div>
          )}
          {!!value.value && (
            <>
              <Separator class="bg-muted" />
              <div class="p-1">
                <Button
                  block
                  variant="subtle"
                  label={$t("clear_filter")}
                  onClick={() => {
                    if (!columnFilter.value) return;
                    value.value = "";
                    focusOnInput();
                  }}
                ></Button>
              </div>
            </>
          )}
        </Dropdown.Group>
      </FacetedFilter>
    );
  },
});

export function isStringFilter(filter: unknown): filter is StringFilter {
  if (typeof filter !== "object") return false;
  if (!filter) return false;

  return "_type" in filter && (filter as any)._type === "string";
}

export const stringFilter: FilterFn<any> = (row, id, comparator: StringFilter): boolean => {
  if (!comparator) return true;
  let value = row.getValue(id) as string;

  if (typeof value !== "string") return true;
  value = value.toLowerCase() as string;

  if (comparator.operator === "eq") return value === comparator.value.toLowerCase();
  if (comparator.operator === "neq") return value !== comparator.value;
  if (comparator.operator === "like") return value.includes(comparator.value.toLowerCase());
  if (comparator.operator === "nlike") return !value.includes(comparator.value.toLowerCase());
  if (comparator.operator === "regexp") return new RegExp(comparator.value).test(value);

  return true;
};
